export type PromptSaveStatus = "idle" | "pending" | "saving" | "saved" | "error"

type PromptSaveContext = {
  taskId: string
  saved: string | null
  latest: string
  target: string
  request: Promise<boolean> | null
}

export const createHotVideoPromptSaver = ({
  save,
  onChange,
}: {
  save: (params: API.Task.SaveOptimizePromptParams) => Promise<void>
  onChange: (status: PromptSaveStatus, error: string) => void
}) => {
  let current: PromptSaveContext | null = null
  const saveTarget = async (context: PromptSaveContext): Promise<boolean> => {
    if (current !== context) return false
    if (context.target === context.saved) {
      onChange(context.latest === context.saved ? "saved" : "pending", "")
      return true
    }

    const prompt = context.target
    onChange("saving", "")
    try {
      await save({ task_id: context.taskId, result_prompt: prompt })
      if (current !== context) return false
      context.saved = prompt
      // Only another explicit save can queue a write; typing alone must wait.
      return saveTarget(context)
    } catch (error) {
      // A lost response can still mean the server accepted the write.
      context.saved = null
      if (current === context)
        onChange(
          "error",
          error instanceof Error ? error.message : "请检查网络后重试",
        )
      return false
    }
  }

  const flush = (): Promise<boolean> => {
    const context = current
    if (!context) return Promise.resolve(true)
    context.target = context.latest
    if (context.request) return context.request
    if (context.latest === context.saved) return Promise.resolve(true)

    context.request = saveTarget(context).finally(() => {
      context.request = null
    })
    return context.request
  }

  return {
    reset: (taskId = "", prompt = ""): void => {
      current = taskId
        ? {
            taskId,
            saved: prompt,
            latest: prompt,
            target: prompt,
            request: null,
          }
        : null
      onChange("idle", "")
    },
    update: (prompt: string): void => {
      const context = current
      if (!context || context.latest === prompt) return
      context.latest = prompt
      if (context.request) return
      if (context.latest === context.saved) {
        onChange("saved", "")
        return
      }
      onChange("pending", "")
    },
    flush,
  }
}
